import InsufficientPointsError from "@/errors/InsufficientPointsError";

export class AiPointService {
    private baseUrl: string;

    private getUserPointsEndpoint = '/v2/ai/point/{username}';
    private reservePointsEndpoint = '/v2/ai/point/{username}/reservation';
    private consumePointsEndpoint = '/v2/ai/point/{username}/reservation/{reservation_id}/consume';
    private cancelPointsEndpoint = '/v2/ai/point/{username}/reservation/{reservation_id}/cancel';
    private eventId = 5;
    private timeout = 10000; // Timeout in milliseconds (10 seconds)

    constructor(baseUrl: string) {
        this.baseUrl = baseUrl;
    }

    async getUserPoints(username: string): Promise<number> {
        const endpointPath = this.getUserPointsEndpoint.replace('{username}', encodeURIComponent(username));
        const url = `${this.baseUrl}${endpointPath}`;

        const headers = {
            'Content-Type': 'application/json',
        };

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), this.timeout);

        try {
            const response = await fetch(url, {
                method: 'GET',
                headers: headers,
                signal: controller.signal,
            });

            clearTimeout(timeoutId);

            if (response.status === 404) {
                return 0;
            }

            if (!response.ok) {
                console.error(`Failed to get points for ${username}. Status: ${response.status}`);
                throw new Error(`Failed to get points. Status: ${response.status}`);
            }

            return await response.json().then((data) => {
                if (data && data.balance !== undefined) { 
                    return data.balance;
                }
                throw new Error('Invalid response format: points not found.');
            });

        } catch (error) {
            clearTimeout(timeoutId);

            if (error instanceof Error && error.name === 'AbortError') {
                console.error(`Request timed out after ${this.timeout}ms for getUserPoints:`, url);
                throw new Error(`Request timed out: ${url}`);
            } else {
                console.error(`Error getting points for user ${username}:`, error);

                if (error instanceof Error) {
                    throw new Error(`Failed to get points: ${error.message}`);
                } else {
                    throw new Error(`Failed to get points due to an unknown error.`);
                }
            }
        }
    }

    async reservePoints(username: string): Promise<string> {
        const endpointPath = this.reservePointsEndpoint.replace('{username}', encodeURIComponent(username));
        const url = `${this.baseUrl}${endpointPath}`;

        const headers = {
            'Content-Type': 'application/json',
        };

        const body = JSON.stringify({
            event_id: this.eventId,
        });

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), this.timeout);

        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: headers,
                body: body,
                signal: controller.signal,
            });

            clearTimeout(timeoutId);

            if (response.status === 402) {
                throw new InsufficientPointsError('Insufficient points for this operation.');
            }

            if (!response.ok) {
                console.error(`Failed to reserve points for ${username}. Status: ${response.status}`);
                throw new Error(`Failed to reserve points. Status: ${response.status}`);
            }

            return await response.json().then((data) => {
                if (data && data.reservation_id) {
                    return data.reservation_id;
                }
                throw new Error('Invalid response format: reservation_id not found.');
            });

        } catch (error) {
            clearTimeout(timeoutId);

            if (error instanceof Error && error.name === 'AbortError') {
                console.error(`Request timed out after ${this.timeout}ms for reservePoints:`, url);
                throw new Error(`Request timed out: ${url}`);
            }
            else if (error instanceof InsufficientPointsError) {
                throw error;
            }
            else {
                console.error(`Error reserve points for user ${username}:`, error);

                if (error instanceof Error) {
                    throw new Error(`Failed to reserve points: ${error.message}`);
                } else {
                    throw new Error(`Failed to reserve points due to an unknown error.`);
                }
            }
        }
    }

    async consumePoints(username: string, reservationId: string): Promise<Response> {
        const endpointPath = this.consumePointsEndpoint.replace('{username}', encodeURIComponent(username))
            .replace('{reservation_id}', encodeURIComponent(reservationId));
        const url = `${this.baseUrl}${endpointPath}`;

        const headers = {
            'Content-Type': 'application/json',
        };

        const body = JSON.stringify({
            event_id: this.eventId,
        });

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), this.timeout);

        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: headers,
                body: body,
                signal: controller.signal,
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                console.error(`Failed to cancel points for ${username}. Status: ${response.status}`);
                throw new Error(`Failed to cancel points. Status: ${response.status}`);
            }

            return response;

        } catch (error) {
            clearTimeout(timeoutId);

            if (error instanceof Error && error.name === 'AbortError') {
                console.error(`Request timed out after ${this.timeout}ms for cancelPoints:`, url);
                throw new Error(`Request timed out: ${url}`);
            } else {
                console.error(`Error cancelling points for user ${username}:`, error);

                if (error instanceof Error) {
                    throw new Error(`Failed to cancel points: ${error.message}`);
                } else {
                    throw new Error(`Failed to cancel points due to an unknown error.`);
                }
            }
        }
    }

    async cancelPoints(username: string, reservationId: string): Promise<Response> {
        const endpointPath = this.cancelPointsEndpoint.replace('{username}', encodeURIComponent(username))
            .replace('{reservation_id}', encodeURIComponent(reservationId));
        const url = `${this.baseUrl}${endpointPath}`;

        const headers = {
            'Content-Type': 'application/json',
        };

        const body = JSON.stringify({
            event_id: this.eventId,
        });

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), this.timeout);

        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: headers,
                body: body,
                signal: controller.signal,
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                console.error(`Failed to consume points for ${username}. Status: ${response.status}`);
                throw new Error(`Failed to consume points. Status: ${response.status}`);
            }

            return response;

        } catch (error) {
            clearTimeout(timeoutId);

            if (error instanceof Error && error.name === 'AbortError') {
                console.error(`Request timed out after ${this.timeout}ms for consumePoints:`, url);
                throw new Error(`Request timed out: ${url}`);
            } else {
                console.error(`Error consuming points for user ${username}:`, error);

                if (error instanceof Error) {
                    throw new Error(`Failed to consume points: ${error.message}`);
                } else {
                    throw new Error(`Failed to consume points due to an unknown error.`);
                }
            }
        }
    }
}
